import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  // Navigation & Page State
  const [currentPage, setCurrentPage] = useState('welcome'); // 'welcome' or 'workspace'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayPage, setDisplayPage] = useState('welcome'); // actual rendered page (for transition timing)
  const [greeting, setGreeting] = useState('Welcome back');
  
  // Dashboard Task Checklist State
  const [tasks, setTasks] = useState([
    { id: 1, text: "Configure global CSS design system & typography", completed: true, category: "Design" },
    { id: 2, text: "Construct responsive state-driven page routes", completed: true, category: "Core" },
    { id: 3, text: "Implement glowing ambient backdrop glow shaders", completed: false, category: "Aesthetics" },
    { id: 4, text: "Wire interactive task progress tracker metric", completed: false, category: "Logic" },
  ]);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState('Core');
  const [activityLogs, setActivityLogs] = useState([
    { id: 1, action: "Initialized workspace core framework", time: "Just now" },
    { id: 2, action: "Design variables injected successfully", time: "5m ago" }
  ]);

  // Accent Color Theme Customization State
  const [activeAccent, setActiveAccent] = useState('indigo'); // 'indigo', 'emerald', 'cyan', 'rose'

  // Dynamic greeting based on time of day
  useEffect(() => {
    const hours = new Date().getHours();
    if (hours < 12) setGreeting('Good Morning');
    else if (hours < 17) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, []);

  // Handle page transition animations
  const navigateTo = (page) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPage(page);
      setDisplayPage(page);
      setIsTransitioning(false);
    }, 500); // match fade-out animation length
  };

  // Toggle single task completion
  const handleToggleTask = (id) => {
    setTasks(tasks.map(task => {
      if (task.id === id) {
        const nextState = !task.completed;
        // Log action in log widget
        const newLog = {
          id: Date.now(),
          action: `${nextState ? 'Completed' : 'Reopened'} "${task.text.substring(0, 25)}..."`,
          time: "1s ago"
        };
        setActivityLogs(prev => [newLog, ...prev.slice(0, 4)]);
        return { ...task, completed: nextState };
      }
      return task;
    }));
  };

  // Add new task
  const handleAddTask = (e) => {
    e.preventDefault();
    if (!newTaskText.trim()) return;
    const newTask = {
      id: Date.now(),
      text: newTaskText.trim(),
      completed: false,
      category: newTaskCategory
    };
    setTasks([...tasks, newTask]);
    
    const newLog = {
      id: Date.now(),
      action: `Created task "${newTaskText.substring(0, 25)}..."`,
      time: "Just now"
    };
    setActivityLogs(prev => [newLog, ...prev.slice(0, 4)]);
    setNewTaskText('');
  };

  // Delete task
  const handleDeleteTask = (id, text) => {
    setTasks(tasks.filter(task => task.id !== id));
    const newLog = {
      id: Date.now(),
      action: `Deleted task "${text.substring(0, 25)}..."`,
      time: "Just now"
    };
    setActivityLogs(prev => [newLog, ...prev.slice(0, 4)]);
  };

  // Clear completed tasks
  const handleClearCompleted = () => {
    setTasks(tasks.filter(task => !task.completed));
    const newLog = {
      id: Date.now(),
      action: `Purged completed tasks`,
      time: "Just now"
    };
    setActivityLogs(prev => [newLog, ...prev.slice(0, 4)]);
  };

  // Stats calculation
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.completed).length;
  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className={`app-root theme-${activeAccent} page-${currentPage}`}>
      {/* Background Glow Blobs */}
      <div className="ambient-glow-container">
        <div className="glow-blob glow-blob-1"></div>
        <div className="glow-blob glow-blob-2"></div>
        <div className="glow-blob glow-blob-3"></div>
      </div>

      <main className="content-container">
        {displayPage === 'welcome' ? (
          /* ================= PAGE 1: WELCOME SCREEN ================= */
          <div className={`page-wrapper welcome-page ${isTransitioning ? 'fade-out-down' : 'fade-in-up'}`}>
            <div className="hero-badge">
              <span className="badge-glow"></span>
              <span className="badge-text">v2.1 Stable Release</span>
            </div>
            
            <h1 className="hero-title">
              DANA<span className="text-gradient-primary">BOOKS</span>.
            </h1>
            
            <p className="hero-subtitle">
              Welcome to the ultimate creative suite. Seamlessly transition from abstract ideas to production-grade architecture. Click below to experience your next generation workspace.
            </p>

            <div className="hero-cta-container">
              <button 
                id="btn-enter-workspace"
                className="cta-button" 
                onClick={() => navigateTo('workspace')}
              >
                Enter Workspace
                <svg className="cta-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>

            <div className="features-preview-grid">
              <div className="preview-card">
                <div className="card-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-indigo)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </div>
                <h3>Unified Dashboard</h3>
                <p>Track metrics, manage files, and keep milestones organized in a modern glass portal.</p>
              </div>
              
              <div className="preview-card">
                <div className="card-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                    <polyline points="2 17 12 22 22 17"></polyline>
                    <polyline points="2 12 12 17 22 12"></polyline>
                  </svg>
                </div>
                <h3>Dynamic Customization</h3>
                <p>Swap neon accent color gradients instantly. Adapt the canvas to match your mood.</p>
              </div>

              <div className="preview-card">
                <div className="card-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-rose)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h3>High-Fidelity Tracking</h3>
                <p>Interactive item checklist integrated with a dynamic progress bar tracking productivity in real-time.</p>
              </div>
            </div>
          </div>
        ) : (
          /* ================= PAGE 2: WORKSPACE DASHBOARD ================= */
          <div className={`page-wrapper dashboard-page ${isTransitioning ? 'fade-out-down' : 'fade-in-up'}`}>
            
            {/* Top Navigation Bar */}
            <header className="dashboard-header glass-panel">
              <div className="header-left">
                <div className="workspace-logo">
                  <span className="logo-dot"></span>
                  <h2>AETHER</h2>
                </div>
                <div className="greeting-box">
                  <span className="greeting-label">{greeting}, Operator</span>
                  <span className="date-label">{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                </div>
              </div>
              
              <div className="header-right">
                {/* Theme Selector */}
                <div className="theme-selector-panel">
                  <span className="theme-txt">Accent:</span>
                  <div className="theme-circles">
                    <button 
                      title="Indigo Theme"
                      className={`theme-circle accent-indigo-btn ${activeAccent === 'indigo' ? 'active' : ''}`}
                      onClick={() => setActiveAccent('indigo')}
                    ></button>
                    <button 
                      title="Emerald Theme"
                      className={`theme-circle accent-emerald-btn ${activeAccent === 'emerald' ? 'active' : ''}`}
                      onClick={() => setActiveAccent('emerald')}
                    ></button>
                    <button 
                      title="Cyan Theme"
                      className={`theme-circle accent-cyan-btn ${activeAccent === 'cyan' ? 'active' : ''}`}
                      onClick={() => setActiveAccent('cyan')}
                    ></button>
                    <button 
                      title="Rose Theme"
                      className={`theme-circle accent-rose-btn ${activeAccent === 'rose' ? 'active' : ''}`}
                      onClick={() => setActiveAccent('rose')}
                    ></button>
                  </div>
                </div>

                <button 
                  id="btn-exit-workspace"
                  className="exit-button" 
                  onClick={() => navigateTo('welcome')}
                >
                  <svg className="exit-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                  Exit Portal
                </button>
              </div>
            </header>

            {/* Dashboard Grid Layout */}
            <div className="dashboard-grid">
              
              {/* Left Column: Progress & Tasks Checklist */}
              <div className="dashboard-main-col">
                
                {/* Progress Metric Card */}
                <section className="metric-overview-card glass-panel">
                  <div className="progress-stats-row">
                    <div>
                      <span className="card-lbl">WORKSPACE HEALTH</span>
                      <h3>Performance Tracker</h3>
                    </div>
                    <div className="percentage-display text-gradient-primary">
                      {completionPercentage}%
                    </div>
                  </div>
                  <div className="progress-track-bg">
                    <div 
                      className="progress-track-fill" 
                      style={{ width: `${completionPercentage}%` }}
                    ></div>
                  </div>
                  <div className="progress-details">
                    <span><strong>{completedTasks}</strong> of <strong>{totalTasks}</strong> deliverables complete</span>
                    <span>{completionPercentage === 100 ? "Excellent. All tasks finished!" : "Project runs smoothly"}</span>
                  </div>
                </section>

                {/* Main Task Manager Widget */}
                <section className="tasks-manager-card glass-panel">
                  <div className="card-header-row">
                    <div>
                      <span className="card-lbl">MILESTONES & TASKS</span>
                      <h3>Active Checklist</h3>
                    </div>
                    <button className="clear-btn" onClick={handleClearCompleted} disabled={completedTasks === 0}>
                      Clear Completed
                    </button>
                  </div>

                  {/* Task List */}
                  <div className="tasks-list-container">
                    {tasks.length === 0 ? (
                      <div className="empty-tasks-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
                          <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                        </svg>
                        <p>No active workspace tasks. Create some below!</p>
                      </div>
                    ) : (
                      tasks.map(task => (
                        <div 
                          key={task.id} 
                          className={`task-row ${task.completed ? 'task-completed' : ''}`}
                          onClick={() => handleToggleTask(task.id)}
                        >
                          <div className="task-checkbox-col">
                            <div className="custom-checkbox">
                              {task.completed && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              )}
                            </div>
                          </div>
                          
                          <div className="task-content-col">
                            <span className="task-text">{task.text}</span>
                            <span className={`task-tag tag-${task.category.toLowerCase()}`}>{task.category}</span>
                          </div>

                          <button 
                            className="task-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTask(task.id, task.text);
                            }}
                            title="Delete Task"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Task Form */}
                  <form onSubmit={handleAddTask} className="task-creation-form">
                    <input 
                      type="text" 
                      placeholder="Add new task to workspace..." 
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      className="task-input"
                    />
                    
                    <select 
                      value={newTaskCategory}
                      onChange={(e) => setNewTaskCategory(e.target.value)}
                      className="category-select"
                    >
                      <option value="Core">Core</option>
                      <option value="Design">Design</option>
                      <option value="Logic">Logic</option>
                      <option value="Aesthetics">Aesthetics</option>
                      <option value="QA">QA</option>
                    </select>

                    <button type="submit" className="add-task-btn" disabled={!newTaskText.trim()}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add
                    </button>
                  </form>
                </section>
              </div>

              {/* Right Column: Activity Grid & Quick Links */}
              <div className="dashboard-side-col">
                
                {/* Visual Contribution Calendar Widget */}
                <section className="activity-calendar-card glass-panel">
                  <span className="card-lbl">PRODUCTIVITY FREQUENCY</span>
                  <h3>Activity Matrix</h3>
                  <div className="activity-matrix-grid">
                    {Array.from({ length: 35 }).map((_, idx) => {
                      // color block logic based on completed tasks count or pseudo-randomness
                      let level = 0;
                      if (idx === 4 || idx === 12 || idx === 18) level = 3;
                      else if (idx === 10 || idx === 22 || idx === 27) level = 2;
                      else if (idx === 1 || idx === 8 || idx === 15 || idx === 32) level = 1;
                      
                      // Highlight current completed items dynamically on grid
                      if (completedTasks > 0 && idx === 34) level = 4;
                      if (completedTasks > 2 && idx === 33) level = 3;
                      if (completedTasks > 4 && idx === 30) level = 2;

                      return (
                        <div 
                          key={idx} 
                          className={`matrix-node node-level-${level}`}
                          title={`Productivity score: Level ${level}`}
                        ></div>
                      );
                    })}
                  </div>
                  <div className="matrix-legend">
                    <span>Less</span>
                    <div className="legend-nodes">
                      <div className="matrix-node node-level-0"></div>
                      <div className="matrix-node node-level-1"></div>
                      <div className="matrix-node node-level-2"></div>
                      <div className="matrix-node node-level-3"></div>
                      <div className="matrix-node node-level-4"></div>
                    </div>
                    <span>More</span>
                  </div>
                </section>

                {/* Audit Logs / Activity Log Widget */}
                <section className="activity-log-card glass-panel">
                  <span className="card-lbl">SYSTEM LOGS</span>
                  <h3>Live Workspace Feed</h3>
                  <div className="logs-feed-container">
                    {activityLogs.map(log => (
                      <div key={log.id} className="log-item">
                        <div className="log-indicator-dot"></div>
                        <div className="log-content">
                          <p>{log.action}</p>
                          <span className="log-time">{log.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Quick Shortcuts Panel */}
                <section className="workspace-shortcuts-card glass-panel">
                  <span className="card-lbl">INTEGRATED LINKS</span>
                  <h3>Environment Access</h3>
                  <div className="shortcut-buttons-grid">
                    <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="shortcut-link">
                      GitHub Console
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                    <a href="https://react.dev" target="_blank" rel="noopener noreferrer" className="shortcut-link">
                      React Core Docs
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </section>
              </div>

            </div>

          </div>
        )}
      </main>
    </div>
  );
}

export default App;
